/* eslint-disable react/forbid-component-props */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useLocalization } from '@/data/Localization';
import { Grid, Paper, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { ContentRecommendation } from '../ContentRecommendation';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { StoreLocatorCard } from './parts/StoreLocatorCard';
import {
	streetPageBackToWinkelSX,
	streetPageEspotChildSX,
	streetPageEspotsSX,
	streetPageStoreClosedColorSX,
} from './styles/storeLocatorLeaf';
import { storeStructuredData as defaultStoreStructuredData } from '@/data/constants/structuredData';
import { useSettings } from '@/data/Settings';
import { useStoreLocatorStreetPage } from '@/data/Content/StoreLocatorStreetPage';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { CityStreet } from '@/data/types/Store';

const days = {
	mon: 'Monday',
	tue: 'Tuesday',
	wed: 'Wednesday',
	thu: 'Thursday',
	fri: 'Friday',
	sat: 'Saturday',
	sun: 'Sunday',
} as const;

// eslint-disable-next-line complexity
export const StoreLocatorStreet = () => {
	const { settings } = useSettings();
	const basePath = useMemo(() => settings.userData.SiteLink_DomainName, [settings]);
	const router = useNextRouter();
	const routes = useLocalization('Routes');
	const localization = useLocalization('StoreLocator');
	const path = usePathname();

	const cityStreet: CityStreet = useMemo(() => {
		const pathArr = path?.split('/');
		return {
			street: pathArr?.at(-1)?.trim() as string,
			city: pathArr?.at(-2)?.replace('speelgoedwinkel-', '')?.trim() as string,
		};
	}, [path]);

	const { storeStreetData, nearbyStores, isRedirect } = useStoreLocatorStreetPage(cityStreet);

	const {
		isClickCollectAllowed,
		lessSpecialTimes,
		storeClosedToday,
		specialHoursToday,
		todayTimes,
		specialEvents,
		isDHLServicePoint,
		isNintendoCorner,
		isFanafdeling
	} = storeStreetData;

	const openingTimesDisplayValues: Record<string, string | null> = useMemo(
		() =>
			(storeStreetData.openingTimes || []).reduce(
				(result, timeOfWeek) => {
					const day = (timeOfWeek.name || '').slice(-3);

					if (day in days && timeOfWeek.name === `opentime${day}` && timeOfWeek.displayValue) {
						result[day as keyof typeof days] = timeOfWeek.displayValue;
					}

					return result;
				},
				Object.keys(days).reduce(
					(result, day) => ({
						...result,
						[day]: null,
					}),
					{}
				) as Record<keyof typeof days, string | null>
			),
		[storeStreetData.openingTimes]
	);
let storeTimingsData: { "@type": string; dayOfWeek: "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday"; opens: string; closes: string; }[] = []; 
const storeStructuredData = useMemo(() => {
		let storeStructuredDataFinal = defaultStoreStructuredData;
		 if(!isRedirect) storeStructuredDataFinal = storeStructuredDataFinal
			.replaceAll('replaceFullSeoUrl', basePath + path)
			.replaceAll('replaceStoreName', storeStreetData?.storeName??''.trim() ?? '')
			.replace('replaceAddr1', storeStreetData?.city??''.trim() ?? '')
			.replace('replaceZipCode', storeStreetData?.postalCode??''.trim() ?? '')
			.replace('replaceStreetHouseNum', storeStreetData?.displayAddress??''.trim() ?? '')
			.replace('replacePhone', storeStreetData?.phone??''.trim() ?? '')
			.replace('replaceState', storeStreetData?.state ?? ''); 
			 
		let geoDetails = '';
		if (storeStreetData?.coordinates?.lat && storeStreetData?.coordinates?.lng) {
			geoDetails = `{"@type": "GeoCoordinates","latitude": ${storeStreetData?.coordinates?.lat},"longitude": ${storeStreetData?.coordinates?.lng}}`;
		} else {
			geoDetails = `{"@type": "GeoCoordinates","latitude": 0,"longitude": 0}`;
		}
		storeStructuredDataFinal = storeStructuredDataFinal.replace('geoDetails', geoDetails);	
		Object.entries(days).forEach(([day, dayFull]) => {
			const openTime = openingTimesDisplayValues[day];
			const isOpen = !!(openTime && openTime !== 'Gesloten');

			if (isOpen) {
				storeTimingsData.push({"@type": "OpeningHoursSpecification","dayOfWeek": dayFull,"opens": openTime.split('-')[0].trim(),"closes": openTime.split('-')[1].trim()});
			}
		});
		storeStructuredDataFinal = storeStructuredDataFinal
				 	.replace("replaceOpeningHoursSpecificationList",JSON.stringify(storeTimingsData));

		return storeStructuredDataFinal;
	}, [
		basePath,
		openingTimesDisplayValues,
		path,
		storeStreetData?.city,
		storeStreetData?.coordinates?.lat,
		storeStreetData?.coordinates?.lng,
		storeStreetData?.displayAddress,
		storeStreetData?.phone,
		storeStreetData?.postalCode,
		storeStreetData?.storeName,
	]);

	const onBackToStloc = useCallback(() => {
		router.push(routes.StoreLocator.route.t() + '/speelgoedwinkel-' + cityStreet.city);
	}, [cityStreet.city, router, routes.StoreLocator.route]);

	useEffect(() => {
		if (isRedirect) onBackToStloc();
	}, [isRedirect, onBackToStloc]);

	return storeStreetData?.storeName || storeStreetData?.physicalStoreName ? (
		<Paper sx={{ padding: '15px;' }}>
			<Typography onClick={onBackToStloc} sx={streetPageBackToWinkelSX}>
				{localization.storeStreetBackLink.t()}{' '}
			</Typography>
			<Grid container>
				<Grid xs={12}>
					<Typography variant="h1">
						{localization.storeStreetHeader.t({ street: `${storeStreetData?.storeName}` })}
					</Typography>
				</Grid>
				<Grid xs={12} md={6}>
					<Typography variant="h3" sx={{ marginTop: '30px;' }}>
						{storeStreetData?.storeName}{' '}
					</Typography>
					<Typography className="storeAddressCS" sx={{ marginTop: '30px;' }}>
						{storeStreetData?.displayAddress}{' '}
					</Typography>
					<Typography className="storeAddressCS">
						{storeStreetData?.postalCode} {storeStreetData?.city}{' '}
					</Typography>
					<Typography className="storeAddressCS">{storeStreetData?.phone} </Typography>

					{isClickCollectAllowed ? (
						<Typography sx={{ marginTop: '30px;' }}>
							{localization.storeStreetClickNCollectAllowed.t()}
						</Typography>
					) : null}
					{isDHLServicePoint ? (
						<Typography sx={{ marginTop: '5px;' }}>
							{localization.storeStreetDHLServicePoint.t()}
						</Typography>
					) : null}
					{isNintendoCorner ? (
						<Typography sx={{ marginTop: '5px;' }}>
							{localization.storeStreetNintendoCorner.t()}
						</Typography>
					) : null}
					{isFanafdeling ? (
						<Typography sx={{ marginTop: '5px;' }}>
							{localization.storeStreetFanafdeling.t()}
						</Typography>
					) : null}
					{storeClosedToday ? (
						<Typography sx={streetPageStoreClosedColorSX}>
							{localization.storeStreetClosedToday.t()}{' '}
						</Typography>
					) : specialHoursToday && specialHoursToday.displayValue.toLowerCase() === 'gesloten' ? (
						<Typography sx={streetPageStoreClosedColorSX}>
							{localization.storeStreetClosedToday.t()}{' '}
						</Typography>
					) : specialHoursToday && specialHoursToday.value !== '0' ? (
						<Typography sx={{ color: '#368632;' }}>
							{localization.storeStreetOpenTodayFromTo.t({
								from: specialHoursToday.displayValue.replace('-', ' tot '),
							})}{' '}
						</Typography>
					) : todayTimes && todayTimes.displayValue !== 'Gesloten' ? (
						<Typography sx={{ color: '#368632;' }}>
							{localization.storeStreetOpenTodayFromTo.t({
								from: todayTimes.displayValue.replace('-', ' tot '),
							})}{' '}
						</Typography>
					) : (
						<Typography sx={streetPageStoreClosedColorSX}>
							{localization.storeStreetClosedToday.t()}{' '}
						</Typography>
					)}
				</Grid>
				<Grid xs={12} md={6}>
					<Typography variant="h3" sx={{ marginTop: '30px;' }}>
						{localization.storeStreetOpeningHours.t()}{' '}
					</Typography>
					<Grid container spacing={2} sx={{ marginTop: '10px;' }}>
						<Grid item xs={6} md={4} lg={6}>
							<Typography>{localization.storeStreetMonday.t()}</Typography>
						</Grid>
						<Grid item xs={6} className="storeTime">
							{openingTimesDisplayValues.mon}
						</Grid>
						<Grid item xs={6} md={4} lg={6}>
							<Typography>{localization.storeStreetTuesday.t()}</Typography>
						</Grid>
						<Grid item xs={6} className="storeTime">
							{openingTimesDisplayValues.tue}
						</Grid>
						<Grid item xs={6} md={4} lg={6}>
							<Typography>{localization.storeStreetWednesday.t()}</Typography>
						</Grid>
						<Grid item xs={6} className="storeTime">
							{openingTimesDisplayValues.wed}
						</Grid>
						<Grid item xs={6} md={4} lg={6}>
							<Typography>{localization.storeStreetThursday.t()}</Typography>
						</Grid>
						<Grid item xs={6} className="storeTime">
							{openingTimesDisplayValues.thu}
						</Grid>
						<Grid item xs={6} md={4} lg={6}>
							<Typography>{localization.storeStreetFriday.t()}</Typography>
						</Grid>
						<Grid item xs={6} className="storeTime">
							{openingTimesDisplayValues.fri}
						</Grid>
						<Grid item xs={6} md={4} lg={6}>
							<Typography>{localization.storeStreetSaturday.t()}</Typography>
						</Grid>
						<Grid item xs={6} className="storeTime">
							{openingTimesDisplayValues.sat}
						</Grid>
						<Grid item xs={6} md={4} lg={6}>
							<Typography>{localization.storeStreetSunday.t()} </Typography>
						</Grid>
						<Grid item xs={6} className="storeTime">
							{openingTimesDisplayValues.sun}
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Stack py={1}>
				{(lessSpecialTimes ? lessSpecialTimes.length > 0 : false) ? (
					<Typography variant="h3" sx={{ marginTop: '30px' }}>
						{localization.storeStreetSpecialOpeningHours.t()}
					</Typography>
				) : null}
				<Grid container>
					{lessSpecialTimes?.map(({ displayName, displayValue }) => (
						<Grid
							item
							sx={{ marginTop: '15px' }}
							xs={12}
							md={6}
							lg={6}
							key={`${displayName}-${displayValue}`}
						>
							<Stack direction={'row'} spacing={4}>
								<Typography>
									<strong>{displayName.substring(0, 10)}</strong>
								</Typography>
								<Typography> {displayName.substring(10, displayName.length)} </Typography>
								<Typography> {displayValue}</Typography>
							</Stack>
						</Grid>
					))}
				</Grid>
			</Stack>
			<Stack py={1}>
				{(specialEvents ? specialEvents.length > 0 : false) ? (
					<Typography variant="h3" sx={{ marginTop: '30px' }}>
						{localization.storeStreetEvenementenHours.t()}
					</Typography>
				) : null}
				<Grid container>
					{specialEvents?.map(({ displayName, displayValue }) => (
						<Grid
							item
							sx={{ marginTop: '15px' }}
							xs={12}
							md={6}
							lg={6}
							key={`${displayName}-${displayValue}`}
						>
							<Grid container>
								<Grid item xs={4}>
									<Typography><strong>{displayName.split(" ").at(0)}</strong></Typography>
								</Grid>
								<Grid item xs={4}>
									<Typography> {displayName.split(" ").slice(1).join(" ")} </Typography>
								</Grid>
								<Grid item xs={4}>
									<Typography> {displayValue}</Typography>
								</Grid>
							</Grid>
						</Grid>
					))}
				</Grid>
			</Stack>
			<Stack>
				<Grid container sx={streetPageEspotsSX}>
					<Grid item sx={streetPageEspotChildSX} xs={12} md={6} lg={6}>
						<ContentRecommendation
							id={`${storeStreetData?.physicalStoreName}_StoreLocator_PublicTransport_ESpot`}
							properties={{
								emsName: `${storeStreetData?.physicalStoreName}_StoreLocator_PublicTransport_ESpot`,
							}}
						/>
					</Grid>
					<Grid item sx={streetPageEspotChildSX} xs={12} md={6} lg={6}>
						<ContentRecommendation
							id={`${storeStreetData?.physicalStoreName}_StoreLocator_ParkingOptions_ESpot`}
							properties={{
								emsName: `${storeStreetData?.physicalStoreName}_StoreLocator_ParkingOptions_ESpot`,
							}}
						/>
					</Grid>
				</Grid>
			</Stack>
			<Stack>
				<Typography sx={{ marginTop: '30px' }} variant="h3">
					{localization.storeStreetNearbyHeader.t({ street: `${storeStreetData?.storeName}` })}
				</Typography>
				<Grid container sx={{ marginTop: '15px', marginBottom: '30px' }}>
					<Grid container item spacing={8} alignItems="flex-start">
						{nearbyStores?.map((store) => (
							<>
								{storeStreetData?.storeName !== store.storeName ? (
									<Grid item xs={12} sm={6} md={4} lg={3} key={store.id}>
										<StoreLocatorCard storeDetails={store} />
									</Grid>
								) : null}
							</>
						))}
					</Grid>
				</Grid>
				<ContentRecommendation
					id={`${storeStreetData?.physicalStoreName}_CSD_Footer_Espot`}
					properties={{ emsName: `${storeStreetData?.physicalStoreName}_CSD_Footer_Espot` }}
				/>
			</Stack>
			<br />
			<br />
			<Typography sx={{ visibility: 'hidden' }}>{storeStreetData?.physicalStoreName}</Typography>
			{storeStructuredData ? (
				<script
					id="storeData"
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: storeStructuredData }}
				></script>
			) : null}
		</Paper>
	) : (
		<Typography>
			<ProgressIndicator />
		</Typography>
	);
};
